import React, { createContext, useContext, useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getMe, invalidateMe } from '../services/meService';
import { BYPASS_AUTH } from '../utils/config.js';

// Safe hook: returns a no-op keycloak when auth is bypassed
const useSafeKeycloak = () => {
  if (BYPASS_AUTH) {
    return { initialized: true, keycloak: { authenticated: true } };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useKeycloak();
};

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

const DEFAULT_STATE = {
  // User identity
  id: null,
  email: null,
  firstName: null,
  lastName: null,

  // Authorization (from Keycloak JWT roles)
  authorization: {
    isAuthorized: false,
    isAdmin: false,
    tenantName: null,
    domain: null,
  },

  // Integration / tenant config
  integration: null,
  hasIntegration: false,
  needsTenantSelection: false,
  availableTenants: [],

  // Live Box/Salesforce/Snowflake OAuth connection status — populated independently
  // of `integration`, so it's accurate even before an Integration row exists.
  serviceConnections: { box: false, salesforce: false, snowflake: false },

  // UI state
  isLoading: true,
  error: null,
};

export const AdminProvider = ({ children }) => {
  const { initialized, keycloak } = useSafeKeycloak();
  const [state, setState] = useState(DEFAULT_STATE);

  const loadMe = async (forceRefresh = false) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const me = await getMe({ forceRefresh });

      console.log('[AdminContext] /me/full response:', {
        email: me.email,
        is_admin: me.is_admin,
        roles: me.roles,
      });

      setState({
        id: me.id,
        email: me.email,
        firstName: me.first_name,
        lastName: me.last_name,

        authorization: {
          isAuthorized: true, // Any Keycloak-authenticated user is authorized
          isAdmin: !!me.is_admin,
          tenantName: me.integration?.tenant_name ?? null,
          domain: me.integration?.domain ?? null,
        },

        integration: me.integration ?? null,
        hasIntegration: !!me.has_integration,
        serviceConnections: me.service_connections ?? { box: false, salesforce: false, snowflake: false },
        needsTenantSelection: !!me.needs_tenant_selection,
        availableTenants: me.available_tenants ?? [],

        isLoading: false,
        error: me._error ?? null,
      });
    } catch (error) {
      console.error('[AdminContext] Failed to load /me:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load user data',
      }));
    }
  };

  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      loadMe();
    } else if (initialized && !keycloak.authenticated) {
      setState({ ...DEFAULT_STATE, isLoading: false });
    }
  }, [initialized, keycloak.authenticated]);

  const refreshAfterIntegrationChange = async () => {
    invalidateMe();
    await loadMe(true);
  };

  const value = {
    ...state,

    // Convenience flat accessors (backward compatibility)
    isAuthorized: state.authorization.isAuthorized,
    isAdmin: state.authorization.isAdmin,
    tenantName: state.authorization.tenantName,
    domain: state.authorization.domain,

    // Actions
    refreshAdminStatus: (force = false) => loadMe(force),
    refreshAfterIntegrationChange,

    // Legacy shape (components that destructure authorization directly)
    adminInfo: null,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};